import React from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkActions} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {ListActionDefinitions} from 'src/app/actions/Dashboard/ListActionDefinitions';
import {TasksPendingSyncBanner} from 'src/app/tasks/offline/TasksPendingSyncBanner';
import {TasksOfflineBoundary} from 'src/app/tasks/TasksOfflineBoundary';
import {SupervisorApprovalList} from 'src/app/tasks/SupervisorApprovalList';
import {GetTaskTypeName, TaskPageTitleDict} from 'src/app/tasks/taskHelpers';
import {useTaskPermissions} from 'src/shared/auth/permissions/useTaskPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {useConnectivity} from 'src/offline/useConnectivity';
import {MyActionsTab} from './components/MyActionsTab/MyActionsTab';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export type ActionsDashViewType =
  | 'user'
  | 'manage'
  | 'supervisor-approval'
  | ''
  | ':view';

const ActionsDashPage: React.FC = () => {
  const taskType = TaskTypeEnum.Actions;
  const module = ModuleEnum.Actions;
  const featureFlag = FeatureFlagEnum.Actions;

  useSetDocumentTitle(GetTaskTypeName(taskType));

  const taskPerms = useTaskPermissions(module);

  const navigate = useAppNavigate();
  const {view} = useParams();
  const {isOffline} = useConnectivity();

  // Only the cached "my" list is served offline; a deep link to Manage falls
  // back to it rather than rendering a list that cannot load.
  const selectedView = (isOffline ? 'user' : (view ?? 'user')) as ActionsDashViewType;

  const {RenderedTabsComponent} = useUrlQueryTabV2<ActionsDashViewType>({
    view: selectedView,
    defaultView: 'user',
    tabs: [
      {
        view: 'user',
        label: `My Actions`,
        component: <MyActionsTab />,
      },

      {
        view: 'supervisor-approval',
        label: `Supervisor Approvals`,
        hidden: true, // Not currently supported for actions.
        component: <SupervisorApprovalList taskType={TaskTypeEnum.Actions} />,
      },

      {
        view: 'manage',
        label: `Manage Actions`,
        disabled: !taskPerms.view || isOffline,
        component: <ListActionDefinitions />,
      },
    ],
    onSelectView: view => {
      navigate(PathUtility.Actions(view as ActionsDashViewType));
    },
  });

  return (
    <TasksOfflineBoundary>
    <ContentWrapper featureFlag={featureFlag}>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkActions.icon}>
          {TaskPageTitleDict[taskType].plural}
        </PageHeader.Title>
      </PageHeader>
      <TasksPendingSyncBanner />
      {RenderedTabsComponent}
    </ContentWrapper>
    </TasksOfflineBoundary>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Actions(':view'),
  element: <ActionsDashPage />,
  isPrivate: true,
  supportsOffline: true,
});

export default ActionsDashPage;
